<template>
    <treemap-chart :data="data" :blurredChart="userAgeNotValid" :blurredChartMsg="$t('tests.health-data-agerestriction')"></treemap-chart>
</template>

<script>
import { GREEN_COLOUR, RED_COLOUR, YELLOW_COLOUR, ORANGE_MID_COLOUR, BACKGROUND_COLOUR } from '@/constants/colours.js'
import { GLUCOSE, BODY_MASS_INDEX, HDL_CHOLESTEROL, LDL_CHOLESTEROL, CHOLESTEROL, BLOOD_PRESSURE, TRIGLYCERIDES } from '@/constants/clinicHistory'
import ClinicHistoryRepository from '@/repositories/ClinicHistoryRepository'
import TreemapChart from '@/components/charts/TreemapChart.vue'

export default {
    components: {
        TreemapChart
    },
    data () {
        return {
            properties: [],
            userHistoryValues: {},
            data: [],
            bloodPressureValuesByAgeMan: {
                18: [[91, 121, 145, 150], [46, 72, 96, 101]],
                24: [[91, 125, 149, 154], [48, 74, 98, 103]],
                29: [[94, 125, 149, 154], [51, 75, 99, 104]],
                39: [[96, 131, 155, 160], [54, 78, 102, 107]],
                49: [[96, 136, 160, 165], [56, 82, 106, 111]],
                59: [[101, 141, 165, 170], [56, 84, 108, 113]],
                67: [[101, 146, 170, 175], [56, 86, 110, 115]]
            },
            bloodPressureValuesByAgeWoman: {
                18: [[86, 116, 140, 145], [46, 71, 95, 100]],
                24: [[86, 116, 140, 145], [46, 71, 95, 100]],
                29: [[88, 121, 145, 150], [46, 72, 96, 101]],
                39: [[91, 125, 149, 154], [51, 75, 99, 104]],
                49: [[91, 136, 160, 165], [51, 82, 106, 111]],
                59: [[96, 141, 165, 170], [56, 84, 108, 113]],
                67: [[101, 146, 170, 175], [56, 86, 110, 115]]
            },
            referenceValues: {
                [BODY_MASS_INDEX]: [18.5, 25.5, 26.9, 29.9],
                [BLOOD_PRESSURE]: [],
                [GLUCOSE]: [72, 100, 125, 0],
                [TRIGLYCERIDES]: [0, 109, 149, 199],
                [CHOLESTEROL]: [0, 200, 239, 0]
            },
            dataSignificance: {
                [BODY_MASS_INDEX]: 6,
                [BLOOD_PRESSURE]: 5,
                [GLUCOSE]: 3,
                [TRIGLYCERIDES]: 3,
                [CHOLESTEROL]: 3
            }
        }
    },
    created () {
        ClinicHistoryRepository().getAllVariables().then((response) => {
            this.userHistoryValues = response.data.data.basicVariables
            this.calculateCholesterol()
            this.properties = Object.keys(this.referenceValues)
            this.properties.forEach((x) => {
                var completed = this.checkValueSet(x)
                const t = {
                    name: this.$t(x),
                    tooltipText: completed ? `${this.userHistoryValues[x].values[0]} ${this.measureUnit(x)}` : '-',
                    value: this.dataSignificance[x],
                    color: this.setValueColor(x),
                    completed: completed,
                    route: '/patient/clinic-history/health-data'
                }
                this.data.push(t)
            })
        })
    },
    methods: {
        setValueColor (property) {
            if (!this.checkValueSet(property)) return BACKGROUND_COLOUR
            if (property === BLOOD_PRESSURE) return this.setBloodPressureValues()
            var value = this.userHistoryValues[property].values[0]
            var referenceData = this.referenceValues[property]
            switch (true) {
                case value < referenceData[0] || (referenceData[3] !== 0 ? value > referenceData[3] : value > referenceData[2]):
                    return RED_COLOUR
                case value <= referenceData[1]:
                    return GREEN_COLOUR
                case value <= referenceData[2]:
                    return YELLOW_COLOUR
                case value <= referenceData[3]:
                    return ORANGE_MID_COLOUR
            }
        },
        checkValueSet (property) {
            return !(this.userHistoryValues[property].values.length === 0 || this.userHistoryValues[property].values[0] === 0 || this.userHistoryValues[property].values[0] === '/')
        },
        measureUnit (property) {
            const unitMsgKey = `measureUnits.${property}`
            return !this.$t(unitMsgKey).includes('measureUnits.') ? ` ${this.$t(unitMsgKey)}` : ''
        },
        variableHasNoValueSet (property) {
            return this.userHistoryValues[property].values.length === 0
        },
        setBloodPressureValues () {
            var ageInRange = Object.keys(this.bloodPressureValuesByAgeMan).find(age => age <= this.$store.state.session.user.age)
            var sysDiaValues = this.userHistoryValues[BLOOD_PRESSURE].values[0].split('/')
            var sys = sysDiaValues[0]
            var dia = sysDiaValues[1]
            if (this.$store.state.session.user.sex === 'H') {
                this.referenceValues[BLOOD_PRESSURE] = this.bloodPressureValuesByAgeMan[ageInRange]
            } else {
                this.referenceValues[BLOOD_PRESSURE] = this.bloodPressureValuesByAgeWoman[ageInRange]
            }
            var sysReferenceData = this.referenceValues[BLOOD_PRESSURE][0]
            var diaReferenceData = this.referenceValues[BLOOD_PRESSURE][1]
            switch (true) {
                case sys < sysReferenceData[0] || sys > sysReferenceData[3] || dia < diaReferenceData[0] || dia > diaReferenceData[3]:
                    return RED_COLOUR
                case sys <= sysReferenceData[1] && dia <= diaReferenceData[1]:
                    return GREEN_COLOUR
                case sys <= sysReferenceData[2] && dia <= diaReferenceData[2]:
                    return YELLOW_COLOUR
                case sys <= sysReferenceData[3] && dia <= diaReferenceData[3]:
                    return ORANGE_MID_COLOUR
            }
        },
        calculateCholesterol () {
            if (this.checkValueSet(LDL_CHOLESTEROL) && this.checkValueSet(HDL_CHOLESTEROL) && this.checkValueSet(TRIGLYCERIDES)) {
                this.userHistoryValues[CHOLESTEROL] = {values: [this.userHistoryValues[LDL_CHOLESTEROL].values[0] + this.userHistoryValues[HDL_CHOLESTEROL].values[0] + Math.round(this.userHistoryValues[TRIGLYCERIDES].values[0] / 5)]}
            } else {
                this.userHistoryValues[CHOLESTEROL] = {values: []}
            }
        }
    },
    computed: {
        userAgeNotValid () {
            const age = this.$store.state.session.user.age
            return age <= 18 || age >= 67
        }
    }
}
</script>
<style>
    .highcharts-tooltip {
        z-index: 9999 !important;
    }
    .in-chart-icon {
      filter: invert(38%) brightness(70%) saturate(100%);
      object-fit: contain;
    }
</style>


// WEBPACK FOOTER //
// src/components/charts/HealthDataDiagram.vue